/**
 * Scenario Builder
 *
 * Interactive lever panel: utilisation, rate, headcount, product sales
 * and cost levers with live-computed NPBT vs the target margin.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import type { ChangeEvent } from 'react';
import { computeScenario, adjustLabourForHeadcount } from '@/utils/scenarioMath';
import { formatCurrency } from '@/utils/formatters';
import type {
  LabourCostModel,
  ScenarioDrivers,
  ScenarioInput,
} from '@/types/profitScenario';

export interface LoadedScenario {
  id: string;
  name: string;
  description?: string;
  drivers: ScenarioDrivers;
}

interface ScenarioBuilderProps {
  baselineDrivers: ScenarioDrivers;
  targetMarginPercent: number;
  labourModel: LabourCostModel;
  loadedScenario: LoadedScenario | null;
  mutating: boolean;
  mutateError: string | null;
  onCreate: (input: ScenarioInput) => Promise<boolean>;
  onUpdate: (id: string, input: ScenarioInput) => Promise<boolean>;
  onClearLoaded: () => void;
}

interface LeverProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  format: (v: number) => string;
  baseline: number;
  onChange: (v: number) => void;
}

function Lever({
  label, value, min, max, step, format, baseline, onChange,
}: LeverProps) {
  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => onChange(Number(e.target.value)),
    [onChange]
  );
  const delta = value - baseline;

  return (
    <label className="ps-lever">
      <span className="ps-lever__head">
        <span className="ps-lever__label">{label}</span>
        <span className="ps-lever__value">
          {format(value)}
          {delta !== 0 && (
            <span className={`ps-lever__delta ${delta > 0 ? 'ps-up' : 'ps-down'}`}>
              {delta > 0 ? '+' : ''}{format(delta)}
            </span>
          )}
        </span>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={handleChange}
        className="ps-lever__slider"
        aria-label={label}
      />
    </label>
  );
}

export function ScenarioBuilder({
  baselineDrivers,
  targetMarginPercent,
  labourModel,
  loadedScenario,
  mutating,
  mutateError,
  onCreate,
  onUpdate,
  onClearLoaded,
}: ScenarioBuilderProps) {
  const [drivers, setDrivers] = useState<ScenarioDrivers>(baselineDrivers);
  const [name, setName] = useState('');
  const [saved, setSaved] = useState(false);

  // Background refetches give baselineDrivers a new object identity on
  // every mutation/refresh; keep the latest in a ref so lever state only
  // resets on explicit load/clear actions, never wiping in-progress edits.
  const baselineRef = useRef(baselineDrivers);
  baselineRef.current = baselineDrivers;
  const prevLoadedIdRef = useRef<string | null>(null);

  useEffect(() => {
    const prevId = prevLoadedIdRef.current;
    if (loadedScenario) {
      setDrivers(loadedScenario.drivers);
      setName(loadedScenario.name);
      setSaved(false);
    } else if (prevId !== null) {
      // Explicit "New from baseline" after having a scenario loaded
      setDrivers(baselineRef.current);
      setName('');
      setSaved(false);
    }
    prevLoadedIdRef.current = loadedScenario?.id ?? null;
  }, [loadedScenario]);

  const set = useCallback(
    (key: keyof ScenarioDrivers) => (v: number) =>
      setDrivers((d) => ({ ...d, [key]: v })),
    []
  );

  // Billable-headcount moves scale salaries proportionally: each person
  // added or removed costs costPerBillablePersonMonthly per month. The
  // adjustment is anchored to the current labour value, so a manual
  // salaries edit stays the base until headcount moves again.
  const handleHeadcountChange = useCallback(
    (v: number) =>
      setDrivers((d) => ({
        ...d,
        billableHeadcount: v,
        labourMonthly: adjustLabourForHeadcount(
          d.labourMonthly,
          v - d.billableHeadcount,
          labourModel.costPerBillablePersonMonthly
        ),
      })),
    [labourModel.costPerBillablePersonMonthly]
  );

  const result = useMemo(
    () => computeScenario(drivers, targetMarginPercent),
    [drivers, targetMarginPercent]
  );
  const baseResult = useMemo(
    () => computeScenario(baselineDrivers, targetMarginPercent),
    [baselineDrivers, targetMarginPercent]
  );

  const handleSave = useCallback(async () => {
    setSaved(false);
    const input: ScenarioInput = {
      name: name.trim(),
      // Preserve the existing description on update; the builder has no
      // description editor and omitting it would erase the stored value.
      description: loadedScenario?.description,
      drivers,
    };
    const ok = loadedScenario
      ? await onUpdate(loadedScenario.id, input)
      : await onCreate(input);
    setSaved(ok);
  }, [name, drivers, loadedScenario, onCreate, onUpdate]);

  const fmtPct = (v: number) => `${Math.round(v)}%`;
  const fmtNum = (v: number) => `${Math.round(v * 10) / 10}`;
  const fmtDollar = (v: number) => `$${Math.round(v).toLocaleString()}`;

  return (
    <div className="ps-builder">
      <div className="ps-builder__levers">
        <h4 className="ps-builder__group-title">Revenue levers</h4>
        <Lever label="Billable utilisation" value={drivers.utilisationPercent}
          min={0} max={100} step={1} format={fmtPct}
          baseline={baselineDrivers.utilisationPercent}
          onChange={set('utilisationPercent')} />
        <Lever label="Average hourly rate" value={drivers.averageHourlyRate}
          min={100} max={1000} step={5} format={fmtDollar}
          baseline={baselineDrivers.averageHourlyRate}
          onChange={set('averageHourlyRate')} />
        <Lever label="Billable people" value={drivers.billableHeadcount}
          min={0} max={30} step={0.5} format={fmtNum}
          baseline={baselineDrivers.billableHeadcount}
          onChange={handleHeadcountChange} />
        <Lever label="Hours available / person / month"
          value={drivers.capacityHoursPerPerson}
          min={40} max={200} step={2} format={fmtNum}
          baseline={baselineDrivers.capacityHoursPerPerson}
          onChange={set('capacityHoursPerPerson')} />
        <Lever label="Product sales / month" value={drivers.productRevenueMonthly}
          min={0} max={100000} step={1000} format={fmtDollar}
          baseline={baselineDrivers.productRevenueMonthly}
          onChange={set('productRevenueMonthly')} />
        <Lever label="Other revenue / month" value={drivers.otherRevenueMonthly}
          min={0} max={100000} step={1000} format={fmtDollar}
          baseline={baselineDrivers.otherRevenueMonthly}
          onChange={set('otherRevenueMonthly')} />

        <h4 className="ps-builder__group-title">Cost levers (monthly)</h4>
        <Lever label="Salaries & super" value={drivers.labourMonthly}
          min={0} max={300000} step={1000} format={fmtDollar}
          baseline={baselineDrivers.labourMonthly}
          onChange={set('labourMonthly')} />
        <p className="ps-lever-hint">
          Salaries auto-adjust with billable people at{' '}
          {formatCurrency(labourModel.costPerBillablePersonMonthly)}/person/mo
          {labourModel.source === 'salary-register'
            ? ` (salary register, ${labourModel.registerBillableCount ?? 0} billable)`
            : ' (derived from current labour cost)'}
          . Moving this slider manually sets a new base.
        </p>
        <Lever label="Contractors" value={drivers.contractorMonthly}
          min={0} max={150000} step={1000} format={fmtDollar}
          baseline={baselineDrivers.contractorMonthly}
          onChange={set('contractorMonthly')} />
        <Lever label="Overheads & subscriptions" value={drivers.overheadsMonthly}
          min={0} max={150000} step={1000} format={fmtDollar}
          baseline={baselineDrivers.overheadsMonthly}
          onChange={set('overheadsMonthly')} />
        <Lever label="AI spend" value={drivers.aiMonthly}
          min={0} max={100000} step={500} format={fmtDollar}
          baseline={baselineDrivers.aiMonthly}
          onChange={set('aiMonthly')} />
      </div>

      <div className="ps-builder__results">
        <div className={`ps-outcome ${result.meetsTarget ? 'ps-outcome--good' : 'ps-outcome--bad'}`}>
          <span className="ps-outcome__label">Net profit before tax</span>
          <span className="ps-outcome__value">
            {formatCurrency(result.npbtMonthly)}/mo
          </span>
          <span className="ps-outcome__margin">
            {result.npbtMarginPercent.toFixed(1)}% margin
            {' '}{result.meetsTarget ? 'meets' : 'below'} the{' '}
            {targetMarginPercent}% goal
          </span>
        </div>
        <dl className="ps-results-list">
          <div className="ps-results-row">
            <dt>Billable hours</dt>
            <dd>{Math.round(result.billableHoursMonthly).toLocaleString()} h/mo</dd>
          </div>
          <div className="ps-results-row">
            <dt>Services revenue</dt>
            <dd>{formatCurrency(result.servicesRevenueMonthly)}/mo</dd>
          </div>
          <div className="ps-results-row">
            <dt>Total revenue</dt>
            <dd>
              {formatCurrency(result.totalRevenueMonthly)}/mo
              <span className={result.totalRevenueMonthly >= baseResult.totalRevenueMonthly ? 'ps-up' : 'ps-down'}>
                {' '}({result.totalRevenueMonthly >= baseResult.totalRevenueMonthly ? '+' : ''}
                {formatCurrency(result.totalRevenueMonthly - baseResult.totalRevenueMonthly)})
              </span>
            </dd>
          </div>
          <div className="ps-results-row">
            <dt>Total costs</dt>
            <dd>{formatCurrency(result.totalCostsMonthly)}/mo</dd>
          </div>
          <div className="ps-results-row ps-results-row--strong">
            <dt>Quarter revenue</dt>
            <dd>{formatCurrency(result.totalRevenueQuarter)}</dd>
          </div>
          <div className="ps-results-row ps-results-row--strong">
            <dt>Quarter NPBT</dt>
            <dd>{formatCurrency(result.npbtQuarter)}</dd>
          </div>
        </dl>

        <div className="ps-save">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value.replace(/[<>]/g, ''))}
            placeholder="Scenario name (e.g. 60% utilisation + AI cut)"
            maxLength={120}
            className="ps-save__name"
            aria-label="Scenario name"
          />
          <div className="ps-save__actions">
            <button
              onClick={handleSave}
              disabled={mutating || name.trim() === ''}
              className="ps-save__button"
              type="button"
            >
              {mutating
                ? 'Saving...'
                : loadedScenario ? 'Update scenario' : 'Save scenario'}
            </button>
            {loadedScenario && (
              <button
                onClick={onClearLoaded}
                className="ps-save__secondary"
                type="button"
              >
                New from baseline
              </button>
            )}
            {saved && !mutateError && (
              <span className="ps-save__ok" role="status">Saved</span>
            )}
            {mutateError && (
              <span className="ps-save__error" role="alert">{mutateError}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
